import * as Dialog from "@radix-ui/react-dialog";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { cx } from "../../utils/cx";
import { IconButton } from "../IconButton";
import styles from "./Modal.module.scss";

const CloseGlyph = () => (
    <svg aria-hidden fill="none" height={20} viewBox="0 0 20 20" width={20}>
        <path
            d="M6 6l8 8M14 6l-8 8"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="1.8"
        />
    </svg>
);

export type ModalSize = "sm" | "md" | "lg";

/** Open modals, in the order they opened. Later ones paint above. */
const modalStack: number[] = [];
let nextModalId = 0;

function useModalLayer(open: boolean) {
    const idRef = useRef(0);
    if (idRef.current === 0) idRef.current = ++nextModalId;
    const [layer, setLayer] = useState(0);

    useLayoutEffect(() => {
        const id = idRef.current;
        const remove = () => {
            const index = modalStack.indexOf(id);
            if (index >= 0) modalStack.splice(index, 1);
        };
        if (!open) {
            remove();
            setLayer(0);
            return;
        }
        if (!modalStack.includes(id)) modalStack.push(id);
        setLayer(modalStack.indexOf(id));
        return remove;
    }, [open]);

    return layer;
}

const PORTALLED_MENU_SELECTOR =
    '[data-ui="autocomplete-menu"], [data-ui="product-search-autocomplete-menu"], [data-ui="header-actions-menu"], [data-ui="color-field-popover"], [data-radix-select-content], [data-radix-popper-content-wrapper]';

/** Radix outside events put the real DOM node on `detail.originalEvent`. */
const isOutsidePortalledMenu = (event: {
    target: EventTarget | null;
    detail?: { originalEvent?: Event };
}) => {
    const target =
        (event.detail?.originalEvent?.target as HTMLElement | null) ??
        (event.target as HTMLElement | null);
    return Boolean(target?.closest?.(PORTALLED_MENU_SELECTOR));
};

export interface ModalProps {
    /** Controlled visibility. */
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Dialog title (visible). */
    title: ReactNode;
    /**
     * Optional visible description under the title in the header.
     * When omitted, `assistiveDescription` is exposed only to screen readers.
     */
    description?: ReactNode;
    /**
     * Screen-reader copy when there is no visible `description`.
     */
    assistiveDescription?: ReactNode;
    /** Main content. */
    children: ReactNode;
    /** Footer actions (e.g. `Button` row). Omit for no footer. */
    footer?: ReactNode;
    /** Max width preset. Default `md`. */
    size?: ModalSize;
    /** Renders the header close control. Default `true`. */
    showCloseButton?: boolean;
    /**
     * When `false`, the body does not scroll — children fill height and
     * manage their own overflow (e.g. tab panels). Default `true`.
     */
    bodyScroll?: boolean;
    /**
     * Close when clicking the overlay / outside the panel.
     * Default `false` — dismiss via close button, footer actions, or Escape.
     */
    closeOnOutsideClick?: boolean;
    className?: string;
}

/**
 * Treez DS — **Modal** (centered dialog).
 *
 * Figma documentation page `8467:41171` (`qgFv3OQga6RQ4luvSFuGFP`).
 * Uses `@radix-ui/react-dialog` (focus trap, `Escape`, scroll lock).
 */
export function Modal({
    open,
    onOpenChange,
    title,
    description,
    assistiveDescription = "Dialog content.",
    children,
    footer,
    size = "md",
    showCloseButton = true,
    bodyScroll = true,
    closeOnOutsideClick = false,
    className,
}: ModalProps) {
    const layer = useModalLayer(open);
    const hasVisibleDescription =
        description != null &&
        description !== "" &&
        !(typeof description === "string" && description.trim() === "");
    const hasBody = children != null && children !== false;

    const preventOutsideDismiss = (event: {
        preventDefault: () => void;
        target: EventTarget | null;
        detail?: { originalEvent?: Event };
    }) => {
        // Always keep open when interacting with portalled menus/popovers.
        if (isOutsidePortalledMenu(event)) {
            event.preventDefault();
            return;
        }
        if (!closeOnOutsideClick) {
            event.preventDefault();
        }
    };

    return (
        <Dialog.Root modal onOpenChange={onOpenChange} open={open}>
            <Dialog.Portal>
                <Dialog.Overlay
                    className={styles.overlay}
                    style={{
                        zIndex: `calc(var(--ds-z-modal) + ${layer * 10})`,
                    }}
                />
                <Dialog.Content
                    className={cx(styles.panel, className)}
                    data-size={size}
                    data-ui="modal"
                    onFocusOutside={event => {
                        // Radix CustomEvents expose the real node on
                        // `detail.originalEvent.target`, not `event.target`.
                        if (isOutsidePortalledMenu(event)) {
                            event.preventDefault();
                        }
                    }}
                    onInteractOutside={preventOutsideDismiss}
                    onPointerDownOutside={preventOutsideDismiss}
                    style={{
                        zIndex: `calc(var(--ds-z-modal) + ${layer * 10 + 1})`,
                    }}
                >
                    <header
                        className={cx(
                            styles.header,
                            !hasBody && styles.headerFlush,
                        )}
                    >
                        <div className={styles.headerText}>
                            <Dialog.Title asChild>
                                <h2 className={styles.title}>{title}</h2>
                            </Dialog.Title>
                            {hasVisibleDescription ? (
                                <Dialog.Description asChild>
                                    <p className={styles.description}>
                                        {description}
                                    </p>
                                </Dialog.Description>
                            ) : (
                                <Dialog.Description className={styles.srOnly}>
                                    {assistiveDescription}
                                </Dialog.Description>
                            )}
                        </div>
                        {showCloseButton ? (
                            <div className={styles.headerActions}>
                                <Dialog.Close asChild>
                                    <IconButton
                                        aria-label="Close dialog"
                                        size="sm"
                                        type="button"
                                        variant="tertiary"
                                    >
                                        <CloseGlyph />
                                    </IconButton>
                                </Dialog.Close>
                            </div>
                        ) : null}
                    </header>
                    {hasBody ? (
                        <div
                            className={cx(
                                styles.body,
                                !bodyScroll && styles.bodyFill,
                            )}
                        >
                            <div className={styles.bodyContent}>{children}</div>
                        </div>
                    ) : null}
                    {footer != null ? (
                        <footer className={styles.footer}>{footer}</footer>
                    ) : null}
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

Modal.displayName = "Modal";
